/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
  CssBaseline,
  responsiveFontSizes,
  StyledEngineProvider,
  Theme,
  ThemeProvider,
  useMediaQuery,
} from '@mui/material';
import { useAtomValue } from 'jotai';
import { useState } from 'react';
import { colorModeAtom } from 'jotai/userPreferences';
import { qtmDarkTheme } from '../darkTheme';
import { qtmLightTheme } from '../lightTheme';
import { QtmThemeContext, QtmThemeContextType } from './ThemeContext';
import { QtmThemeProviderProps, QtmThemeSetting } from './ThemeProvider.types';

declare module '@mui/material/styles' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

// const LOCAL_STORAGE_KEY = 'qtm-theme-setting';

function useTheme(themeSetting: QtmThemeSetting): Theme {
  const isDarkModeEnabled = useMediaQuery('(prefers-color-scheme: dark)');

  // If theme setting is system, then derive the theme from media query hook, otherwise, use appropriate theme.
  switch (themeSetting) {
    case 'system':
      return isDarkModeEnabled ? qtmDarkTheme : qtmLightTheme;
    case 'dark':
      return qtmDarkTheme;
    default:
      return qtmLightTheme;
  }
}

/**
 * The Theme Provider component provides a wrapper around Material UI's ThemeProvider, using the dark and light Quantum
 * Themes.  You can pass it `'dark'`, `'light'`, or `'system'` as the theme setting and it will keep track of the currently applied theme.
 * If using `'system'`, the provider will derive the appropriate theme from the user's system setting.  Theme setting is stored
 * in local storage for persistence.
 */
export const QtmThemeProvider = ({ themeSetting, children }: QtmThemeProviderProps) => {
  // Read setting from local storage, or fallback to passed in value, or default to light.
  const initialThemeSetting: QtmThemeSetting = themeSetting ?? 'light';
  // State for holding the current setting
  const [currentThemeSetting, _setThemeSetting] = useState(initialThemeSetting);
  const colorMode = useAtomValue(colorModeAtom);

  // Get the theme object from the setting
  let theme = useTheme(colorMode ?? currentThemeSetting);
  theme = responsiveFontSizes(theme);

  // Create context update function
  const setThemeSetting = (newThemeSetting: QtmThemeSetting) => {
    // localStorage.setItem(LOCAL_STORAGE_KEY, newThemeSetting);
    _setThemeSetting(newThemeSetting);
  };

  // Create context object
  const themeContext: QtmThemeContextType = {
    themeSetting: currentThemeSetting,
    setThemeSetting: setThemeSetting,
  };

  return (
    <QtmThemeContext.Provider value={themeContext}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={{ ...theme }}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </StyledEngineProvider>
    </QtmThemeContext.Provider>
  );
};
